// frontend/src/services/employeeService.js - Comprehensive Employee Service
import { api } from './api';

/**
 * Employee Service
 * Centralized service for all employee-related API calls
 */
class EmployeeService {
  // ==================== DASHBOARD ====================
  
  /**
   * Get comprehensive dashboard data
   */
  async getDashboard() {
    const response = await api.get('/employee/dashboard');
    return response.data;
  }

  /**
   * Get overview data
   */
  async getOverview() {
    const response = await api.get('/employee/overview');
    return response.data;
  }

  // ==================== PROFILE ====================
  
  /**
   * Get employee profile
   */
  async getProfile() {
    const response = await api.get('/employee/profile');
    return response.data;
  }

  /**
   * Update employee profile
   */
  async updateProfile(profileData) {
    const response = await api.put('/employee/profile', profileData);
    return response.data;
  }

  /**
   * Update avatar
   */
  async updateAvatar(formData) {
    const response = await api.post('/employee/profile/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  /**
   * Get profile settings
   */
  async getSettings() {
    const response = await api.get('/employee/profile/settings');
    return response.data;
  }

  /**
   * Update profile settings
   */
  async updateSettings(settings) {
    const response = await api.put('/employee/profile/settings', settings);
    return response.data;
  }

  // ==================== ATTENDANCE ====================
  
  /**
   * Clock in
   */
  async clockIn(formData) {
    const response = await api.post('/employee/attendance/clock-in', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  /**
   * Clock out
   */
  async clockOut(formData) {
    const response = await api.post('/employee/attendance/clock-out', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  /**
   * Get attendance status
   */
  async getAttendanceStatus() {
    const response = await api.get('/employee/attendance/status');
    return response.data;
  }

  /**
   * Get today's attendance
   */
  async getTodayAttendance() {
    const response = await api.get('/employee/attendance/today');
    return response.data;
  }

  /**
   * Get attendance history
   */
  async getAttendanceHistory(params = {}) {
    const response = await api.get('/employee/attendance/history', { params });
    return response.data;
  }

  /**
   * Get attendance summary
   */
  async getAttendanceSummary(params = {}) {
    const response = await api.get('/employee/attendance/summary', { params });
    return response.data;
  }

  /**
   * Start break
   */
  async startBreak(reason) {
    const response = await api.post('/employee/attendance/break/start', { reason });
    return response.data;
  }

  /**
   * End break
   */
  async endBreak() {
    const response = await api.post('/employee/attendance/break/end');
    return response.data;
  }

  /**
   * Get attendance analytics
   */
  async getAttendanceAnalytics(params = {}) {
    const response = await api.get('/employee/attendance/analytics', { params });
    return response.data;
  }

  // ==================== OVERTIME ====================
  
  /**
   * Submit overtime request
   */
  async submitOvertimeRequest(requestData) {
    const response = await api.post('/employee/overtime/request', requestData);
    return response.data;
  }

  /**
   * Get my overtime requests
   */
  async getOvertimeRequests(params = {}) {
    const response = await api.get('/employee/overtime/requests', { params });
    return response.data;
  }

  /**
   * Cancel overtime request
   */
  async cancelOvertimeRequest(requestId) {
    const response = await api.put(`/employee/overtime/requests/${requestId}/cancel`);
    return response.data;
  }

  // ==================== LEAVE REQUESTS ====================
  
  /**
   * Get my leave requests
   */
  async getLeaveRequests(params = {}) {
    const response = await api.get('/employee/leaves', { params });
    return response.data;
  }

  /**
   * Create leave request
   */
  async createLeaveRequest(requestData) {
    const response = await api.post('/employee/leaves', requestData);
    return response.data;
  }

  /**
   * Get leave request details
   */
  async getLeaveRequestDetails(requestId) {
    const response = await api.get(`/employee/leaves/${requestId}`);
    return response.data;
  }

  /**
   * Update leave request
   */
  async updateLeaveRequest(requestId, requestData) {
    const response = await api.put(`/employee/leaves/${requestId}`, requestData);
    return response.data;
  }

  /**
   * Cancel leave request
   */
  async cancelLeaveRequest(requestId) {
    const response = await api.delete(`/employee/leaves/${requestId}`);
    return response.data;
  }

  // ==================== LEAVE BALANCE ====================
  
  /**
   * Get leave balance
   */
  async getLeaveBalance(params = {}) {
    const response = await api.get('/employee/leaves/balance', { params });
    return response.data;
  }

  /**
   * Get balance history
   */
  async getBalanceHistory(params = {}) {
    const response = await api.get('/employee/leaves/balance/history', { params });
    return response.data;
  }

  // ==================== NOTIFICATIONS ====================
  
  /**
   * Get notifications
   */
  async getNotifications(params = {}) {
    const response = await api.get('/employee/notifications', { params });
    return response.data;
  }

  /**
   * Mark notification as read
   */
  async markNotificationAsRead(notificationId) {
    const response = await api.put(`/employee/notifications/${notificationId}/read`);
    return response.data;
  }

  /**
   * Mark all notifications as read
   */
  async markAllNotificationsAsRead() {
    const response = await api.put('/employee/notifications/mark-all-read');
    return response.data;
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId) {
    const response = await api.delete(`/employee/notifications/${notificationId}`);
    return response.data;
  }

  // ==================== REPORTS & EXPORTS ====================
  
  /**
   * Export attendance report
   */
  async exportAttendanceReport(params = {}) {
    const response = await api.get('/employee/reports/attendance', { 
      params,
      responseType: 'blob' 
    });
    return response.data;
  }

  /**
   * Export leave report
   */
  async exportLeaveReport(params = {}) {
    const response = await api.get('/employee/reports/leaves', { 
      params,
      responseType: 'blob' 
    });
    return response.data;
  }

  /**
   * Export timesheet
   */
  async exportTimesheet(params = {}) {
    const response = await api.get('/employee/reports/timesheet', { 
      params,
      responseType: 'blob' 
    });
    return response.data;
  }

  // ==================== LOCATION ====================
  
  /**
   * Update location
   */
  async updateLocation(locationData) {
    const response = await api.post('/employee/location/update', locationData);
    return response.data;
  }

  /**
   * Get location history
   */
  async getLocationHistory(params = {}) {
    const response = await api.get('/employee/location/history', { params });
    return response.data;
  }

  /**
   * Get current location
   */
  async getCurrentLocation() {
    const response = await api.get('/employee/location/current');
    return response.data;
  }

  // ==================== UTILITY METHODS ====================
  
  /**
   * Format time duration
   */
  formatDuration(minutes) {
    if (!minutes || minutes <= 0) return '0h 0m';
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    return `${hours}h ${remainingMinutes}m`;
  }

  /**
   * Format time ago
   */
  formatTimeAgo(timestamp) {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = now - time;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  }

  /**
   * Format date
   */
  formatDate(date, options = {}) {
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    return new Date(date).toLocaleDateString('en-US', { ...defaultOptions, ...options });
  }

  /**
   * Format time
   */
  formatTime(time, options = {}) {
    const defaultOptions = {
      hour: '2-digit',
      minute: '2-digit'
    };
    
    return new Date(time).toLocaleTimeString('en-US', { ...defaultOptions, ...options });
  }
}

// Create singleton instance
const employeeService = new EmployeeService();

export default employeeService;